import moment from "moment";
import {DomainAncestor, entityConfig, fieldInfo} from "core/common/domain/DomainAncestor";
import {observable, ObservableMap} from "mobx";
import {ID} from "core/metamodel/metaConstant";
import {LovItem} from "core/metamodel/mdEntity";
import { Document, DOCUMENTATTACHMENTTYPE } from 'core/document/documentDomain';
import {lovValue} from "core/form/formUtils";
import documentService from "core/document/documentService";
import {SEVERITY, Tuple} from "core/common/domain/commonDomain";
import {EntityStatus, HistoryObjectField} from "core/event/eventDomain";
import {DEFAULT_CURRENCY_FORMAT, formatNumber} from "core/common/utils/numberUtils";
import {DynamicField, initDynamicFields} from "../../core/metamodel/metaDomain";
import { User } from '../../core/security/securityDomain';

// INVOICE STATUS
// issued:  CONCEPT -> ISSUED (-> CANCELED)
// received:  RECEIVED -> BOOKED (-> CANCELED)

export enum INVOICESTATUS {
  CONCEPT = "CONCEPT",
  ISSUED = "ISSUED",
  CANCELED = "CANCELED",
  RECEIVED = "RECEIVED",
  BOOKED = "BOOKED",
}


export const DEFAULT_MONETARY_ROUNDING = 6;

// INVOICE ITEM TYPE
export enum INVOICEITEMTYPE {
  FREEITEM = "FREEITEM",
  PRODUCTITEM = "PRODUCTITEM",
  PRODUCTVEHICLEITEM = "PRODUCTVEHICLEITEM",
  ROUNDINGDIFF = "ROUNDINGDIFF",
}


// INVOICE_TYPE
export enum INVOICE_TYPE {
  FI = 1,
  CN = 2,
  PR = 3,
  DP = 4,
  AD = 5,
}


export enum PAYMENT_TYPE {
  ACCOUNT_TRANSFER = 1,
  CASH = 2,
  CREDIT_NOTE = 3,
  MUTUAL_CREDIT = 4,
  CUSTOMER_CREDIT = 5
}

export enum INVOICERELATIONTYPE {
  CN_TO_FI = "CN_TO_FI",
  CN_TO_AD = "CN_TO_AD",

  DP_TO_FI = "DP_TO_FI",
  PR_TO_FI = "PR_TO_FI",

  AD_TO_DP = "AD_TO_DP",
  AD_TO_FI = "AD_TO_FI"
}

export const relationTypesToFinalInvoice: Record<INVOICE_TYPE, INVOICERELATIONTYPE[]> = {
  [INVOICE_TYPE.CN] : [INVOICERELATIONTYPE.CN_TO_FI, INVOICERELATIONTYPE.CN_TO_AD],
  [INVOICE_TYPE.PR] : [INVOICERELATIONTYPE.PR_TO_FI],
  [INVOICE_TYPE.DP] : [INVOICERELATIONTYPE.DP_TO_FI],
  [INVOICE_TYPE.AD] : [INVOICERELATIONTYPE.AD_TO_FI, INVOICERELATIONTYPE.AD_TO_DP],
  [INVOICE_TYPE.FI] : []
}

export const linkingPerInvoiceTypes: Record<INVOICE_TYPE, INVOICE_TYPE[]> = {
  [INVOICE_TYPE.CN] : [INVOICE_TYPE.FI, INVOICE_TYPE.AD],
  [INVOICE_TYPE.PR] : [],
  [INVOICE_TYPE.DP] : [],
  [INVOICE_TYPE.AD] : [INVOICE_TYPE.DP],
  [INVOICE_TYPE.FI] : [INVOICE_TYPE.PR, INVOICE_TYPE.DP, INVOICE_TYPE.AD]
}

export const relationArray: Record<INVOICERELATIONTYPE, INVOICE_TYPE[]> = {
  [INVOICERELATIONTYPE.CN_TO_FI]: [INVOICE_TYPE.CN, INVOICE_TYPE.FI],
  [INVOICERELATIONTYPE.CN_TO_AD]: [INVOICE_TYPE.CN, INVOICE_TYPE.AD],

  [INVOICERELATIONTYPE.DP_TO_FI]: [INVOICE_TYPE.DP, INVOICE_TYPE.FI],
  [INVOICERELATIONTYPE.PR_TO_FI]: [INVOICE_TYPE.PR, INVOICE_TYPE.FI],

  [INVOICERELATIONTYPE.AD_TO_FI]: [INVOICE_TYPE.AD, INVOICE_TYPE.FI],
  [INVOICERELATIONTYPE.AD_TO_DP]: [INVOICE_TYPE.AD, INVOICE_TYPE.DP],
}

export function relationArrayInverse(invoiceTypeFrom: INVOICE_TYPE, invoiceTypeTo: INVOICE_TYPE): INVOICERELATIONTYPE {
  for (const [key, value] of Object.entries(relationArray)) {
    if (value[0] === invoiceTypeFrom && value[1] === invoiceTypeTo) {
      return key as INVOICERELATIONTYPE;
    }
  }
  return undefined;
}


export enum PAYMENTRELATIONTYPE {
  PAY_TO_INV = "PAY_TO_INV",
  PAY_TO_AD = "PAY_TO_AD"

}


export enum INVOICEPAYMENTMATCHTYPE {
  VS = "VS",
  BANKACCOUNT = "BANKACCOUNT",
  BANKACCOUNT_AMOUNT = "BANKACCOUNT_AMOUNT",
  CREDIT_NOTE = "CREDIT_NOTE",
  MANUAL = "MANUAL",
}

export enum INVOICEWARNING {
  differentFields = "differentFields",
  differentVatRates = "differentVatRates",
  vehicleOwners = "vehicleOwners",
  exchangeRateDifferent = "exchangeRateDifferent",
  negativeItemExists = "negativeItemExists",
  positiveItemExists = "positiveItemExists",
}




@entityConfig()
export class InvoiceItem extends DomainAncestor {

  @observable invoiceItemId: number;
  @observable invoice: LovItem;
  @observable itemIndex: number;
  @observable itemType: LovItem;
  @observable product: LovItem;
  @observable vehicle: LovItem;
  @observable party: LovItem;
  @observable productNumber: string;
  @observable description: string;
  @observable unitOfMeasure: LovItem;
  @observable vatRateRef: LovItem;
  @observable quantity: number;
  @observable unitPrice: number;
  @observable unitPriceLcy: number;
  @observable basePrice: number;
  @observable basePriceLcy: number;
  @observable adjustmentKoef: number;
  @observable adjustmentAmount: number;
  @observable adjustmentAmountLcy: number;
  @observable basePriceWithAdjustment: number;
  @observable basePriceWithAdjustmentLcy: number;
  @observable invoiceAdjustmentAmount: number;
  @observable invoiceAdjustmentAmountLcy: number;
  @observable priceWithoutVat: number;
  @observable priceWithoutVatLcy: number;
  @observable vatRate: number;
  @observable vatAmount: number;
  @observable vatAmountLcy: number;
  @observable itemPrice: number;
  @observable itemPriceLcy: number;
  @observable comment: string;
  @observable taxType?: LovItem;



  constructor(json: any) {
    super(json);
  }


  public getId(): ID {
    return this.invoiceItemId;
  }

  public setId(id: ID): void {
    this.invoiceItemId = <any>id;
  }



}


@entityConfig()
export class InvoiceVatRecap extends DomainAncestor {

  @observable basis: number;
  @observable basisLcy: number;
  @observable amount: number;
  @observable amountLcy: number;

  constructor(json: any) {
    super(json);
  }


}


@entityConfig()
export class VatRateValue extends DomainAncestor {

  @observable vatRate: number;
  @observable validFrom: string|Date;
  @observable validTo: string|Date;

  constructor(json: any) {
    super(json);
  }
}



@entityConfig()
export class VatRate extends DomainAncestor {

  @observable vatRateId: number;
  @observable name: string;
  @observable validFrom: string|Date;
  @observable validTo: string|Date;
  @observable vatRateCountry: LovItem;
  @observable noVat: boolean;

  @observable @fieldInfo(VatRateValue) values: VatRateValue[];


  constructor(json: any) {
    super(json);

    if (!this.values) this.values = [];
  }


}

export type InvoiceInInvoiceRelationType = LovItem<number>
  & {invoiceType?: LovItem,
  invoiceNumber?: string,
  invoiceDate?: string|Date,
  totalAdjustedPrice?: number,
  invoiceCurrency?: LovItem<string>};

@entityConfig()
export class InvoiceRelation extends DomainAncestor {
  @observable fromInvoice: InvoiceInInvoiceRelationType;
  @observable toInvoice: InvoiceInInvoiceRelationType;
  @observable relationType: LovItem<INVOICERELATIONTYPE>;
  @observable currency: LovItem<string>;
  @observable contributedAmount: number;
  @observable valueDate: string|Date;

  constructor(json: Partial<InvoiceRelation>) {
    super(json);
  }

  getOtherInvoice(invoice: Invoice): InvoiceRelation["fromInvoice"] {
    return (this.fromInvoice && invoice.invoiceId == this.fromInvoice.value) ? this.toInvoice // == - porebuju aby se null rovnalo undefined
      : ( (this.toInvoice && invoice.invoiceId == this.toInvoice.value) ? this.fromInvoice :undefined);
  }

}


@entityConfig()
export class PaymentRelation extends DomainAncestor {
  @observable payment: LovItem<number>;
  @observable invoice: LovItem<number>;
  @observable relationType: LovItem<PAYMENTRELATIONTYPE>;
  @observable currency: LovItem<string>;
  @observable contributedAmount: number;
  @observable valueDate: string|Date;

  constructor(json: any) {
    super(json);
  }
}




@entityConfig()
export class Invoice extends DomainAncestor {

  @observable invoiceId: number;
  @observable invoiceNumber: string;
  @observable invoiceStatus: string;
  @observable invoiceDirection: string;
  @observable billedFrom: LovItem;
  @observable billedTo: LovItem & {defaultAttachmentType?: DOCUMENTATTACHMENTTYPE};
  @observable billedTo2: LovItem;
  @observable profitCenter: LovItem;
  @observable invoiceDate: string|Date;
  @observable invoiceTime: string;
  @observable taxDate: string|Date;
  @observable dueDate: string|Date;
  @observable deliveryDate: string|Date;
  @observable deliveryNoteDate: string|Date;
  @observable orderDate: string|Date;
  @observable startInvoicePeriod: string|Date;
  @observable endInvoicePeriod: string|Date;
  @observable periodicInvoice: boolean;
  @observable orderNumber: string;
  @observable contractNumber: string;
  @observable deliveryNoteNumber: string;
  @observable comment: string;
  @observable internalComment: string;
  @observable paymentCond: LovItem;
  @observable paymentType: LovItem;
  @observable localCurrency: LovItem;
  @observable invoiceCurrency: LovItem<string>;
  @observable invoiceType: LovItem;
  @observable language: LovItem;
  @observable copiedFromInvoiceId: number;
  @observable sendType: LovItem;

  @observable monetaryRounding: number;
  @observable exchangeRate: number;
  @observable basePrice: number;
  @observable basePriceLcy: number;
  @observable itemAdjustmentKoef: number;
  @observable itemAdjustmentAmount: number;
  @observable itemAdjustmentAmountLcy: number;
  @observable basePriceWithAdjustment: number;
  @observable basePriceWithAdjustmentLcy: number;
  @observable invoiceAdjustmentKoef: number;
  @observable invoiceAdjustmentAmount: number;
  @observable invoiceAdjustmentAmountLcy: number;
  @observable priceWithoutVat: number;
  @observable priceWithoutVatLcy: number;
  @observable vatAmount: number;
  @observable vatAmountLcy: number;
  @observable totalPrice: number;
  @observable totalPriceLcy: number;
  @observable pennyAdjustment: number;
  @observable pennyAdjustmentLcy: number;
  @observable totalAdjustedPrice: number;
  @observable totalAdjustedPriceLcy: number;

  @observable dateEmailedToCustomer: string|Date;
  @observable dateSentByPostToCustomer: string|Date;
  @observable dateSentToAccounting: string|Date;
  @observable dateSentToTaxOffice: string|Date;
  @observable dateSentToArchive: string|Date;

  @observable taxOfficeConfirmationNumber: string;
  @observable taxOfficeConfirmationTime: string|Date;
  @observable taxOfficeConfirmationError: string;


  @observable paidAmount: number;
  @observable invoicePaid: string|Date;
  @observable invoiceCredited: string|Date;
  @observable issueDate: string|Date;
  @observable cnOutstanding: number;

  // zkopirovane veci z party
  // party - FROM
  @observable fromFullName: string;
  @observable fromIco: string;
  @observable fromDic: string;
  @observable fromIcoDph: string;
  @observable fromCommercialRegister: string;
  @observable fromPhone: string;
  @observable fromFax: string;
  @observable fromEmail: string;
  @observable fromUrl: string;


  // party - TO
  @observable toFullName: string;
  @observable toIco: string;
  @observable toDic: string;
  @observable toIcoDph: string;


  // adresa - FROM
  @observable fromStreet: string;
  @observable fromCity: string;
  @observable fromZip: string;
  @observable fromCountry: LovItem;


  // adresa - TO PERMANENT
  @observable toPermanentStreet: string;
  @observable toPermanentCity: string;
  @observable toPermanentZip: string;
  @observable toPermanentCountry: LovItem;


  // adresa - TO CORRESPONDENT
  @observable toCorrespondentStreet: string;
  @observable toCorrespondentCity: string;
  @observable toCorrespondentZip: string;
  @observable toCorrespondentCountry: LovItem;


  // FROM BANKA
  @observable fromAccountPrefix: string;
  @observable fromAccountNumber: string;
  @observable fromIban: string;
  @observable fromBankName: string;
  @observable fromBankCode: string;
  @observable fromBankSwift: string;
  @observable fromBankAddress: string;
  @observable fromBankCountry: LovItem;
  @observable fromAccountPrefixLcy: string;
  @observable fromAccountNumberLcy: string;
  @observable fromIbanLcy: string;
  @observable fromBankNameLcy: string;
  @observable fromBankCodeLcy: string;
  @observable fromBankSwiftLcy: string;
  @observable fromBankAddressLcy: string;
  @observable fromBankCountryLcy: LovItem;


  @observable accountingMatchingId: string;

  @observable issuedBy: LovItem;
  @observable orderedBy: LovItem;

  @observable statusFontCss: string;

  @observable entityNumberGen: LovItem;

  @observable accountingNumber: LovItem;

  @observable accountingProfitCenter: LovItem;
  @observable costCenter: LovItem;
  @observable internalOrder: string;
  @observable relatedInvoice: string;

  @observable @fieldInfo(InvoiceVatRecap) vatRecap?: ObservableMap<InvoiceVatRecap>;

  @observable @fieldInfo(InvoiceItem) items: InvoiceItem[];
  @observable @fieldInfo(Document) documents: Document[];
  @observable @fieldInfo(DynamicField) fields: DynamicField[];
  @observable @fieldInfo(InvoiceRelation) fromRels: InvoiceRelation[];
  @observable @fieldInfo(InvoiceRelation) toRels: InvoiceRelation[];


  @observable linkedInvoiceIds: number[];

  @observable $generateInvoiceNumber: boolean;

  @observable $negativeItemExists: boolean;
  @observable $negativeItemResolved: boolean;

  @observable $positiveItemExists: boolean;
  @observable $positiveItemResolved: boolean;

  @observable $exchangeRateResolved: boolean;
  @observable $exchangeRateDifferent: boolean;

  @observable $vatRateResolved: boolean;
  @observable $vatRateDifferent: boolean;


  @observable $differentFields: HistoryObjectField[];

  // Tuple(lovItem(Party of vehicleOwner), list of LovItems of vehicles)
  @observable $vehicleOwners: Tuple<LovItem<number>, LovItem<number>[]>[];
  @observable $vehicleOwnersResolved: boolean;

  @observable $invoiceGraph: InvoiceGraph;


  constructor(json: any) {
    super(json);
  }

  public fromJson(json: any): this {
    super.fromJson(json, "vatRecap");

    if (!this.items) this.items = [];
    if (!this.documents) this.documents = [];
    if (!this.fields) this.fields = [];
    if (!this.fromRels) this.fromRels = [];
    if (!this.toRels) this.toRels = [];

    if (!this.vatRecap) this.vatRecap = new ObservableMap<InvoiceVatRecap>();

    initDynamicFields("Invoice", this);

    return this;
  }


  public getId(): ID {
    return this.invoiceId;
  }

  public setId(id: ID): void {
    super.setId(id);
    this.invoiceId = <any>id;
  }


  public invoiceLabel(): string {
    return this.invoiceNumber;
  }
  public invoiceLov(): LovItem {
    return {value: this.invoiceId, label: this.invoiceLabel()};
  }

  public invoiceIsCurrencyDifferent(): boolean {
    return ( lovValue(this.localCurrency) !== lovValue(this.invoiceCurrency) );
  }

  public invoiceNegativeItemExists() {
    return (this.items.filter(item => item.basePrice < 0).length > 0);
  }

  public invoicePositiveItemExists() {
    return (this.items.filter(item => item.basePrice > 0).length > 0);
  }

  public invoiceComputeHuDeliveryDate() {
    // if  due date is later than period last date
    // then  due date
    // else issue date
    //
    if (this.dueDate && this.endInvoicePeriod) {
      return (moment(this.dueDate).isAfter(moment(this.endInvoicePeriod))) ? this.dueDate : this.invoiceDate;
    } else {
      return null;
    }
  }

  public isSent() {
    return this.dateEmailedToCustomer || this.dateSentByPostToCustomer;
  }

  public getInvoiceParamsForNumberGenSearch() {
    return {
      entityName: "Invoice",
      invoiceDate: this.invoiceDate
    };
  }

  public getFinalInvoiceRels() : InvoiceRelation[]  {
    let typesToFinalInvoice = relationTypesToFinalInvoice[lovValue(this.invoiceType)];
    return this.fromRels.filter(ir => typesToFinalInvoice.includes(lovValue(ir.relationType)));
  }

  public getLinkedInvoices() : LovItem<number>[] {
    return [...this.fromRels.map(r => r.toInvoice), ...this.toRels.map(r => r.fromInvoice)];
  }

  public createInvoiceInInvoiceRelation(): InvoiceInInvoiceRelationType {
    return {
      value: this.invoiceId,
      label: this.label,
      invoiceType: this.invoiceType,
      invoiceNumber: this.invoiceNumber,
      invoiceDate: this.invoiceDate,
      totalAdjustedPrice: this.totalAdjustedPrice
    }
  }



}

export const canDeleteInvoiceDocument = (invoice: Invoice, document: Document): boolean => (invoice.invoiceStatus === INVOICESTATUS.CONCEPT && documentService.isUploadType(document));


export class Rate extends DomainAncestor {
  rate: number;
  currencyPair: LovItem;
  label: string;
  ccy1: string;
  ccy2: string;
  inverted: boolean;

  constructor(json: any) {
    super(json);
  }

}





@entityConfig()
export class IssueInvoicesDto extends DomainAncestor {
  @observable invoiceDate: string|Date;
  @observable taxDate: string|Date;
  @observable startInvoicePeriod: string|Date;
  @observable endInvoicePeriod;
  @observable paymentType: LovItem;
  @observable customerIds: number[];
  @observable profitCenter: LovItem;
  @observable @fieldInfo(EntityStatus) customerStatuses: EntityStatus[];
  @observable issuedBy: LovItem;
  @observable comment: string;
  @observable internalComment: string;
  @observable invoiceAdjustmentKoef: number;

  @observable part: number;
  @observable count: number;
  @observable showErrors: boolean;
  @observable reverseChargeText: string;

  @observable @fieldInfo(Rate) rates: Rate[];


  constructor(json: any) {
    super(json);
    if (!this.rates) this.rates = [];
  }

  customerNoErrorCount() : number {
    return (this.customerStatuses && this.customerStatuses.length) ? this.customerStatuses.filter(cs => cs.status !== SEVERITY.ERROR).length : 0;
  }

}









@entityConfig()
export class InvoiceTemplateItem extends DomainAncestor {

  @observable invoiceTemplateItemId: number;
  @observable invoiceTemplate: LovItem;
  @observable itemIndex: number;
  @observable itemType: LovItem;
  @observable product: LovItem;
  @observable vehicle: LovItem;
  @observable party: LovItem;
  @observable productNumber: string;
  @observable description: string;
  @observable unitOfMeasure: LovItem;
  @observable vatRateRef: LovItem;
  @observable quantity: number;
  @observable unitPrice: number;
  @observable unitPriceExpression: string;
  @observable basePrice: number;
  @observable basePriceExpression: string;
  @observable adjustmentKoef: number;
  @observable adjustmentAmount: number;
  @observable basePriceWithAdjustment: number;
  @observable invoiceAdjustmentAmount: number;
  @observable priceWithoutVat: number;
  @observable vatRate: number;
  @observable vatAmount: number;
  @observable itemPrice: number;
  @observable comment: string;
  @observable taxType?: LovItem;

  @observable $useExpression: boolean;

  constructor(json: any) {
    super(json);
  }


  public getId(): ID {
    return this.invoiceTemplateItemId;
  }

  public setId(id: ID): void {
    this.invoiceTemplateItemId = <any>id;
  }

}




@entityConfig()
export class InvoiceTemplate extends DomainAncestor {

  @observable invoiceTemplateId: number;
  @observable name: string;
  @observable description: string;
  @observable active: boolean;
  @observable closeDate: string|Date;
  @observable billedTo: LovItem;
  @observable billedTo2: LovItem;
  @observable profitCenter: LovItem;
  @observable comment: string;
  @observable internalComment: string;
  @observable paymentCond: LovItem;
  @observable paymentType: LovItem;
  @observable invoiceCurrency: LovItem;
  @observable invoiceType: LovItem;
  @observable language: LovItem;
  @observable eventType: LovItem;

  @observable monetaryRounding: number;
  @observable basePrice: number;
  @observable itemAdjustmentKoef: number;
  @observable itemAdjustmentAmount: number;
  @observable basePriceWithAdjustment: number;
  @observable invoiceAdjustmentKoef: number;
  @observable invoiceAdjustmentAmount: number;
  @observable priceWithoutVat: number;
  @observable vatAmount: number;
  @observable totalPrice: number;
  @observable pennyAdjustment: number;
  @observable totalAdjustedPrice: number;

  @observable validFrom: string|Date;
  @observable validTo: string|Date;
  @observable disableScheduling: boolean;
  @observable remittanceType: LovItem;
  @observable remittanceSkipCount: number;
  @observable holidayHandling: LovItem;
  @observable issueInvoice: boolean;
  @observable markAsSentByPost: boolean;

  @observable issuedBy: LovItem;
  @observable orderedBy: LovItem;

  @observable lastGenerateDate: string|Date;
  @observable nextGenerateDate: string|Date;

  @observable startInvoicePeriodExpression: string;
  @observable endInvoicePeriodExpression: string;
  @observable periodicInvoice: boolean;
  @observable invoiceDateExpression: string;
  @observable taxDateExpression: string;

  @observable entityNumberGen: LovItem;

  @observable accountingNumber: LovItem;
  @observable accountingProfitCenter: LovItem;
  @observable costCenter: LovItem;
  @observable internalOrder: string;

  @observable @fieldInfo(InvoiceTemplateItem) items: InvoiceTemplateItem[];
  @observable @fieldInfo(DynamicField) fields: DynamicField[];

  constructor(json: any) {
    super(json);
  }

  public fromJson(json: any): this {
    super.fromJson(json);

    if (!this.items) this.items = [];
    if (!this.fields) this.fields = [];

    initDynamicFields("InvoiceTemplate", this);

    return this;
  }


  public getId(): ID {
    return this.invoiceTemplateId;
  }

  public setId(id: ID): void {
    this.invoiceTemplateId = <any>id;
    this.id = id;
  }


  invoiceTemplateLov(): LovItem {
    return {value: this.invoiceTemplateId, label: this.name};
  }
}





@entityConfig()
export class Payment extends DomainAncestor {

  @observable paymentId: number;
  @observable valueDate: string|Date;
  @observable amount: number;
  @observable currency: LovItem;
  @observable comment: string;
  @observable paymentType: LovItem;
  @observable bankTransaction: LovItem;
  @observable matchType: INVOICEPAYMENTMATCHTYPE;

  @observable @fieldInfo(PaymentRelation) paymentRels: PaymentRelation[];

  constructor(json: Partial<Payment>) {
    super(json);
  }

  public fromJson(json: any): this {
    super.fromJson(json);

    if (!this.paymentRels) this.paymentRels = [];

    return this;
  }


  public getId(): ID {
    return this.paymentId;
  }

  public setId(id: ID): void {
    this.paymentId = <any>id;
    this.id = id;
  }

  public getFullAmount(): string {
    return formatNumber(this.amount, DEFAULT_CURRENCY_FORMAT) + " " + lovValue(this.currency);
  }
}

export interface InvRelation {
  node: InvoiceGraph;
  relationType: INVOICERELATIONTYPE;
  currency: LovItem<string>;
  contributedAmount: number;
  valueDate: string|Date;
}

export interface PayRelation {
  payment: Payment;
  relationType: PAYMENTRELATIONTYPE;
  currency: LovItem<string>;
  contributedAmount: number;
  valueDate: string|Date;
}

export interface InvoiceGraph {
  invoice: InvoiceInInvoiceRelationType;
  froms: InvRelation[];
  tos: InvRelation[];
  pays: PayRelation[];
  depth: number;
}




// WEBPACK FOOTER //
// ./src/invoice/domain/invoiceDomain.ts